import { Checkbox, Spinner, SpinnerSize } from "@blueprintjs/core";
import { DataTable, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";

import { MetierLocaliteDto, UserMetierDto, UtilisateurApi } from "../../../api";
import { BooleanColumn } from "../../../components";
import { useApiService, useManageError, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IUserMetierLocalitesGridProps {
  metier: UserMetierDto;
  userId: number;
  canEdit: boolean;
}

export const UserMetierLocalitesGrid: React.FunctionComponent<IUserMetierLocalitesGridProps> = ({
  metier,
  userId,
  canEdit
}) => {
  const { t } = useTl();
  const api = useApiService(UtilisateurApi);
  const { manageError } = useManageError();

  const fetchMetiers = React.useCallback(
    () => api.utilisateurGetMetierLocalites({ id: userId, idmetier: metier.idmetier }),
    [api, metier.idmetier, userId]
  );
  const { data, isFetching, isRefetching, refetch } = useQuery(
    ["user-metier-localites", userId, metier.idmetier],
    fetchMetiers
  );

  const tableState = useGridState<MetierLocaliteDto>({
    serverMode: false,
    enableFilter: true,
    pageSize: 15,
    availablePageSizes: [15, 25, 50],
    sortKeys: {
      localite: "ASC",
      codePostal: "ASC"
    },
    storageOptions: {
      columnCustomizationStorageMode: "state"
    }
  });

  React.useEffect(() => {
    if (data) {
      tableState.setData(data);
    }
  }, [data, tableState]);

  const [rowLoading, setRowLoading] = React.useState<number>();
  const toggleSelection = React.useCallback(
    async (item: MetierLocaliteDto) => {
      try {
        setRowLoading(item.idcodePostal);
        await api.utilisateurSaveUserMetierLocalite({ id: userId, idmetier: metier.idmetier, MetierLocaliteDto: item });
      } catch (e) {
        manageError(e);
      } finally {
        await refetch();
        setRowLoading(undefined);
      }
    },
    [api, metier.idmetier, manageError, refetch, userId]
  );

  const columns = React.useMemo<IDataTableColumn<MetierLocaliteDto>[]>(
    () => [
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: MetierLocaliteDto) => {
          if (rowLoading === item.idcodePostal) return <Spinner size={SpinnerSize.SMALL} />;
          return canEdit ? (
            <Checkbox
              checked={item.selected}
              onChange={e => {
                toggleSelection(item);
              }}
            />
          ) : (
            <BooleanColumn value={item.selected} />
          );
        }
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      }
    ],
    [canEdit, rowLoading, t, toggleSelection]
  );

  return (
    <FieldSet title={!!data && t(ETLCodes.NbLocalites, { nb: data.length })}>
      <DataTable
        dateFormat="dd/MM/yyyy"
        tableState={tableState}
        loading={isFetching && !isRefetching}
        columns={columns}
        filterMode="OnEnter"
        keyFieldName="idlocalite"
      />
    </FieldSet>
  );
};

export default UserMetierLocalitesGrid;
